<i18n locale="en">
{
  "close": "Close"
}
</i18n>
<template>
  <!-- Teleport to body so the takeover escapes the app-shell stacking context
       and its z-index outranks body-level portals like the settings modal. -->
  <Teleport to="body">
    <OwnDialog
      v-model="isVisible"
      class="upsell-sheet-dialog"
      position="bottom"
      anchored
      data-testid="upsell-sheet"
      @cancel="onDismiss"
    >
      <div class="upsell-sheet">
        <div class="upsell-sheet__scroll">
          <UpsellTakeover />
        </div>
        <button
          class="upsell-sheet__close"
          type="button"
          :aria-label="t('close')"
          data-testid="upsell-sheet-close-button"
          @click="onDismiss"
        >
          <PhX :size="20" />
        </button>
        <UpsellUpgradeCta
          class="upsell-sheet__cta"
          @upgrade="emit('upgrade')"
        />
      </div>
    </OwnDialog>
  </Teleport>
</template>

<script lang="ts" setup>
import { PhX } from '@phosphor-icons/vue'
import { useEventListener } from '@vueuse/core'
import { useI18n } from 'vue-i18n'

import { OwnDialog } from '@/ui/OwnDialog'

import UpsellTakeover from './UpsellTakeover.vue'
import UpsellUpgradeCta from './UpsellUpgradeCta.vue'

const emit = defineEmits<{
  upgrade: []
}>()

const isVisible = defineModel<boolean>()

const { t } = useI18n()

const onDismiss = (): void => {
  isVisible.value = false
}

// OwnDialog only closes on background click, so wire Escape ourselves.
useEventListener(window, 'keydown', (event: KeyboardEvent) => {
  if (isVisible.value && event.key === 'Escape') {
    onDismiss()
  }
})
</script>

<style lang="scss" scoped>
// The takeover content scrolls inside the sheet while the close button and
// upgrade CTA float on top of it, pinned to the card's corners.
.upsell-sheet-dialog {
  animation: none;

  // Sits above other dialogs (300-399) and the slide-group carousel arrows
  // (9999) so the takeover is never partially occluded by the surface it
  // launched from.
  z-index: 10000;

  // reka-ui's modal dialog (the settings modal) sets `pointer-events: none` on
  // <body> while open and only re-enables it inside its own portal. As a
  // teleported body child outside that portal, the takeover would inherit
  // `none` and let clicks fall through to the settings content beneath it.
  pointer-events: auto;

  :deep(.own-dialog__card) {
    height: 82%;
    max-height: 82%;
    background-color: #0c0e0e;
    // v-motion slides AND fades the card; force it opaque so only the slide
    // remains.
    opacity: 1 !important;
  }

  :deep(.own-dialog__card-content) {
    overflow: hidden;
  }
}

.upsell-sheet {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;
  background-color: #0b0d0c;

  // Film-grain across the entire sheet. The grain asset is black speckles in
  // the alpha channel, so it's inverted to read as light grain on the dark
  // surface; pointer-events stay off so the chrome underneath remains usable.
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 2;
    pointer-events: none;
    background-image: url('../assets/grain.png');
    background-size: 182px 182px;
    filter: invert(1);
    opacity: 0.5;
  }

  // The backdrop lives on the scroll container (content is transparent) so the
  // overscroll rubber-band shows the same gradient instead of a flat seam.
  &__scroll {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    background-color: #0c0e0e;
    background-image: url('../assets/gradient-lines.png');
    background-repeat: no-repeat;
    background-position: top center;
    background-size: 100% auto;
  }

  &__close {
    position: absolute;
    top: 20px;
    right: 20px;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border: none;
    border-radius: 12px;
    cursor: pointer;
    color: #fff;
    background-color: #222524;

    &:hover {
      background-color: #2e3130;
    }
  }

  &__cta {
    position: absolute;
    right: 24px;
    bottom: 24px;
    z-index: 1;
  }
}
</style>
